import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { useHistory } from 'react-router';
import {
    Button,
    FormControl,
    Grid,
    MenuItem,
    Typography,
} from '@mui/material';
import { SyntheticEvent, useEffect, useState } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles } from '@material-ui/core';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { useUnidadeNegocioGuard } from '../../../common/hooks/useUnidadeNegocioGuard';
import {
    selectUnidadeSelecionada,
    selectUnidadesDeNegocioAssociadas,
} from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { GridItemModalProps } from '../../../common/components/cardsGrid/cardsModalGrid';
import { Field, Form } from 'react-final-form';
import QCXFinalDatePickerField from '../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField';
import QCXTextField from '../../../../shared-components/text-field/QCXTextField';
import QCXSelectContaBancariaAutocompleteField from '../../../../shared-components/select-conta-bancaria/QCXSelectContaBancariaAutocompleteField';
import QCXSelectFornecedorAutocomplete from '../../../../shared-components/select-fornecedor-favorecido-autocomplete/QCXSelectFornecedorAutocomplete';
import { useListClientes } from '../../../common/hooks/useListClientes';
import MultiSelectStyled, { SimpleMultiSelectOption } from '../../../common/components/input/multiSelectStyled';
import QCXSelectDespesaReceitaAutocomplete from '../../../../shared-components/select-despesa-receita/QCXSelectDespesaReceitaAutocomplete';
import { generateSolicitacaoPagamentoReport } from './relatorioSolicitacaoPagamento.helpers';
import { fetchByFilter } from '../../../../features/follow-up/followUpAPI';

interface ControlledValue {
    id: string | number;
    value: string;
}

const useStyles = makeStyles({
    root: {
        '& .MuiDialog-paper': {
            minWidth: '50vw',
        },
    },
});

const SolicitacaoPagamentoPage = ({ title, isOpen, closeModal }: GridItemModalProps) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const { keycloak } = useKeycloak();
    const { token } = keycloak;

    keycloak.updateToken(300);

    const selectedUnit = useSelector(selectUnidadeSelecionada);
    const tenantList = useSelector(selectUnidadesDeNegocioAssociadas);
    const [clientes] = useListClientes();

    const [selectedClients, setSelectedClients] = useState<SimpleMultiSelectOption[]>([]);
    const [selectedFornecedores, setSelectedFornecedores] = useState<string>('');
    const [selectedTenants, setSelectedTenants] = useState<SimpleMultiSelectOption[]>([]);
    const [businessError, setBusinessError] = useState(false);
    const [bancoId, setBancoId] = useState<number | null>(null);

    const [selectedDespesaReceitaId, setSelectedDespesaReceitaId] = useState<string>('');

    const [followUpList, setFollowUpList] = useState<ControlledValue[]>([]);
    const [selectedProcesses, setSelectedProcesses] = useState<ControlledValue[]>([]);
    const [isFetchingProcesses, setIsFetchingProcesses] = useState<boolean>(false);

    const [datasLancamento, setDatasLancamento] = useState({ inicio: '', fim: '' });
    const [datasVencimento, setDatasVencimento] = useState({ inicio: '', fim: '' });
    const [datasPagamento, setDatasPagamento] = useState({ inicio: '', fim: '' });

    const [tipo, setTipo] = useState<string>('');

    const [status, setStatus] = useState('');

    useUnidadeNegocioGuard();

    const handleDespesaReceitaChange = (event: React.ChangeEvent<{}>, newValue: any) => {
        setSelectedDespesaReceitaId('');
        if (newValue) {
            setSelectedDespesaReceitaId(newValue?.id);
        }
    };

    const handleTenantsChange = (
        event: React.SyntheticEvent<Element, Event>,
        value: SimpleMultiSelectOption[] | null
    ) => {
        setSelectedTenants(value || []);
    };

    const handleClientsChange = (
        event: React.SyntheticEvent<Element, Event>,
        value: SimpleMultiSelectOption[] | null
    ) => {
        setSelectedClients(value || []);
    };

    const handleChangeBanco = (_event: any, option: any) => {
        setBancoId(option?.id || null);
    };

    const handleTipoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTipo(event.target.value);
    };

    const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStatus(event.target.value);
    };

    const handleDataChange = (tipo: 'lancto' | 'venc' | 'pago', campo: 'inicio' | 'fim', value: string) => {
        const dataIso = value.split('T')[0];
        if (tipo === 'lancto') setDatasLancamento(prev => ({ ...prev, [campo]: dataIso }));
        if (tipo === 'venc') setDatasVencimento(prev => ({ ...prev, [campo]: dataIso }));
        if (tipo === 'pago') setDatasPagamento(prev => ({ ...prev, [campo]: dataIso }));
    };

    const handleSubmit = async () => {
        keycloak.updateToken(300);

        const isDateRangeValid = (range: { inicio: string, fim: string }) => {
            if (!range.inicio && !range.fim) return true; 
            if (range.inicio && range.fim) {
                return new Date(range.inicio) <= new Date(range.fim);
            }
            return false;
        };

        if (!isDateRangeValid(datasLancamento) || !isDateRangeValid(datasVencimento) || !isDateRangeValid(datasPagamento)) {
            dispatch(setErrorFeedback({ message: 'Verifique os períodos de datas informados.' }));
            return;
        }

        if (selectedTenants.length === 0) {
            setBusinessError(true);
            dispatch(setErrorFeedback({ message: 'Selecione pelo menos uma Unidade de Negócio.' }));
            return;
        } else {
            setBusinessError(false);
        }

        if (token && selectedUnit) {
            try {
                const tenantIds = selectedTenants.map((tenant) => tenant.id.toString());
                const clientIds = selectedClients.map((client) => client.id.toString());
                const processIds = selectedProcesses.map(p => p.id.toString());

                const error = await generateSolicitacaoPagamentoReport(
                    token,
                    selectedUnit,
                    tenantIds,
                    clientIds,
                    selectedFornecedores,
                    tipo,
                    status,
                    bancoId,
                    selectedDespesaReceitaId,
                    datasLancamento,
                    datasVencimento,
                    datasPagamento,
                    processIds
                );
                if (error != null) {
                    dispatch(
                        setErrorFeedback({ message: error ? error : 'Erro ao gerar o relatório.', })
                    );
                } else {
                    dispatch(
                        setSuccessFeedback({ message: 'Relatório gerado com sucesso!', })
                    );
                    // closeModal();
                }
            } catch (error) {
                dispatch(
                    setErrorFeedback({
                        message: 'Erro inesperado ao gerar o relatório.',
                    })
                );
            }
        }
        keycloak.updateToken(300);
        history.push('/financeiro/relatorios');
    };

    useEffect(() => {
        const fetchProcessos = async () => {
            if (selectedClients.length > 0) {
                setIsFetchingProcesses(true);
                try {
                    const clientIds = selectedClients.map(c => c.id).join(',');
                    
                    const response = await fetchByFilter([{ name: 'clientIds', value: clientIds }]);
                    
                    const processosMapeados = response.data.map((item: any) => ({
                        id: item.id?.toString() || '',
                        value: item.numero?.toString() || '[Sem número de processo]',
                    }));

                    setFollowUpList(processosMapeados);
                } catch (error) {
                    console.error('Erro ao buscar processos:', error);
                    dispatch(setErrorFeedback({ message: 'Erro ao carregar processos dos clientes.' }));
                    setFollowUpList([]);
                } finally {
                    setIsFetchingProcesses(false);
                }
            } else {
                setFollowUpList([]);
                setSelectedProcesses([]);
            }
        };

        fetchProcessos();
    }, [selectedClients, dispatch]);

    return (
        <Dialog className={classes.root} open={isOpen} onClose={closeModal}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Form onSubmit={() => { }}>
                    {() => (
                        <form>
                            <Grid container spacing={2}>
                                {/* Unidade de Negócio */}
                                <Grid item xs={12}>
                                    <FormControl fullWidth error={businessError}>
                                        <MultiSelectStyled
                                            disablePortal={false}
                                            controlledValues={selectedTenants}
                                            options={tenantList.map((tenant: { id: any; pessoa: { nomeResumido: any } }) => ({
                                                id: tenant.id.toString(),
                                                value: tenant.pessoa.nomeResumido,
                                            }))}
                                            onChangeAction={handleTenantsChange}
                                            label={'Unidade de Negócio'}
                                        ></MultiSelectStyled>
                                        {selectedTenants.length === 0 && (
                                            <Typography variant="body2" color="error">
                                                Este campo é obrigatório
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>

                                {/* Clientes multi-select */}
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <MultiSelectStyled
                                            disablePortal={false}
                                            options={clientes.map((client) => ({
                                                id: client.id ?? '',
                                                value: `(${client.pessoa!.cnpj}) ${client.pessoa!.nome}`,
                                            }))}
                                            onChangeAction={handleClientsChange}
                                            label={'Clientes'}
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Processo select */}
                                <Grid item xs={12} sm={12} md={6}>
                                    <FormControl fullWidth>
                                        <MultiSelectStyled
                                            disablePortal={false}
                                            controlledValues={selectedProcesses.map(p => ({
                                                id: p.id.toString(),
                                                value: p.value
                                            }))}
                                            options={followUpList.map((process) => ({
                                                id: process?.id?.toString() ?? '',
                                                value: process?.value ?? '',
                                            })) || []}
                                            onChangeAction={(event, value) => {
                                                const formattedValues = value ? value.map(v => ({
                                                    id: v.id,
                                                    value: v.value
                                                })) : [];
                                                setSelectedProcesses(formattedValues);
                                            }}
                                            label={'Processos'}
                                            placeholder={
                                                selectedClients.length === 0
                                                    ? "Selecione um Cliente"
                                                    : isFetchingProcesses
                                                        ? "Carregando..."
                                                        : "Selecionar Processos"
                                            }
                                            disabled={selectedClients.length === 0 || isFetchingProcesses}
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Despesa select */}
                                <Grid item xs={12} sm={12} md={6}>
                                    <FormControl fullWidth>
                                        <QCXSelectDespesaReceitaAutocomplete
                                            id="select-field-despesa"
                                            key="select-field-despesa"
                                            name="despesaReceita.id"
                                            label="Despesa"
                                            initialValues={selectedDespesaReceitaId}
                                            disabled={false}
                                            onChange={handleDespesaReceitaChange}
                                            filterParams={undefined}
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Fornecedor select */}
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <QCXSelectFornecedorAutocomplete
                                            id="select-field-fornecedor"
                                            key="select-field-fornecedor"
                                            name="fornecedorId"
                                            label="Fornecedor / Favorecido"
                                            initialValues={selectedFornecedores}
                                            disabled={false}
                                            filterPredicate={() => false}
                                            onChange={(event: React.ChangeEvent<{}>, newValue: any) => {
                                                setSelectedFornecedores('');
                                                if (newValue) {
                                                    setSelectedFornecedores(newValue?.id);
                                                }
                                            }}
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Tipo select */}
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <QCXTextField
                                            select
                                            label="Tipo"
                                            value={tipo}
                                            onChange={handleTipoChange}
                                            fullWidth
                                            maxLength={255}
                                            visible={true}
                                            helperText=""
                                            meta={{}}
                                            disabled={false}
                                        >
                                            <MenuItem value="TODOS">Todos</MenuItem>
                                            <MenuItem value="OPERACIONAL">Operacional</MenuItem>
                                            <MenuItem value="ADMINISTRATIVA">Administrativa</MenuItem>
                                        </QCXTextField>
                                    </FormControl>
                                </Grid>

                                {/* Status select */}
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <QCXTextField
                                            style={{ width: '100%' }}
                                            label="Status"
                                            key="select-status-nf-emitidas"
                                            id="select-status-nf-emitidas"
                                            select
                                            value={status}
                                            onChange={handleStatusChange}
                                            {...({} as any)}
                                        >
                                            <MenuItem value="TODOS">TODOS</MenuItem>
                                            <MenuItem value="SOLICITACAO_PENDENTE">SOLICITACAO PENDENTE</MenuItem>
                                            <MenuItem value="CANCELADO">CANCELADO</MenuItem>
                                            <MenuItem value="APROVADO">APROVADO</MenuItem>
                                            <MenuItem value="AGUARDANDO_AUTORIZAÇÃO_DO_CLIENTE">AGUARDANDO AUTORIZAÇÃO DO CLIENTE</MenuItem>
                                            <MenuItem value="DUPLICADO">DUPLICADO</MenuItem>
                                            <MenuItem value="PAGO">PAGO</MenuItem>
                                        </QCXTextField>
                                    </FormControl>
                                </Grid>

                                {/* Conta Bancária */}
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <QCXSelectContaBancariaAutocompleteField
                                            id="autocomplete-select-conta-bancaria-field"
                                            key="autocomplete-select-conta-bancaria-field"
                                            name="contaBancaria"
                                            label="Conta Bancária"
                                            initialValues={undefined}
                                            filtraControlaSaldo={true}
                                            onChange={handleChangeBanco}
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Data de Lançamento inicio */}
                                <Grid item xs={6}>
                                    <Field
                                        name="lanctoStart"
                                        render={({ input }) => (
                                            <QCXFinalDatePickerField
                                                id=""
                                                name={input.name}
                                                label={'Data de Lançamento - Início'}
                                                required={false}
                                                format="DD/MM/YYYY"
                                                placeholder="DD/MM/YYYY"
                                                onChange={(date: any) => {
                                                    handleDataChange('lancto', 'inicio', date.toISOString());
                                                    input.onChange(date);
                                                }}
                                                validate={undefined}
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Data de Lançamento final */}
                                <Grid item xs={6}>
                                    <Field
                                        name="lanctoEnd"
                                        render={({ input }) => (
                                            <QCXFinalDatePickerField
                                                id=""
                                                name={input.name}
                                                label={'Data de Lançamento - Final'}
                                                required={false}
                                                format="DD/MM/YYYY"
                                                placeholder="DD/MM/YYYY"
                                                onChange={(date: any) => {
                                                    handleDataChange('lancto', 'fim', date.toISOString());
                                                    input.onChange(date);
                                                }}
                                                validate={undefined}
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Data de Vencimento inicio */}
                                <Grid item xs={6}>
                                    <Field
                                        name="vencStart"
                                        render={({ input }) => (
                                            <QCXFinalDatePickerField
                                                id=""
                                                name={input.name}
                                                label={'Data de Vencimento - Início'}
                                                required={false}
                                                format="DD/MM/YYYY"
                                                placeholder="DD/MM/YYYY"
                                                onChange={(date: any) => {
                                                    handleDataChange('venc', 'inicio', date.toISOString());
                                                    input.onChange(date);
                                                }}
                                                validate={undefined}
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Data de Vencimento final */}
                                <Grid item xs={6}>
                                    <Field
                                        name="vencEnd"
                                        render={({ input }) => (
                                            <QCXFinalDatePickerField
                                                id=""
                                                name={input.name}
                                                label={'Data de Vencimento - Final'}
                                                required={false}
                                                format="DD/MM/YYYY"
                                                placeholder="DD/MM/YYYY"
                                                onChange={(date: any) => {
                                                    handleDataChange('venc', 'fim', date.toISOString());
                                                    input.onChange(date);
                                                }}
                                                validate={undefined}
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Data de Pagamento inicio */}
                                <Grid item xs={6}>
                                    <Field
                                        name="pagoStart"
                                        render={({ input }) => (
                                            <QCXFinalDatePickerField
                                                id=""
                                                name={input.name}
                                                label={'Data de Pagamento - Início'}
                                                required={false}
                                                format="DD/MM/YYYY"
                                                placeholder="DD/MM/YYYY"
                                                onChange={(date: any) => {
                                                    handleDataChange('pago', 'inicio', date.toISOString());
                                                    input.onChange(date);
                                                }}
                                                validate={undefined}
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Data de Pagamento final */}
                                <Grid item xs={6}>
                                    <Field
                                        name="pagoEnd"
                                        render={({ input }) => (
                                            <QCXFinalDatePickerField
                                                id=""
                                                name={input.name}
                                                label={'Data de Pagamento - Final'}
                                                required={false}
                                                format="DD/MM/YYYY"
                                                placeholder="DD/MM/YYYY"
                                                onChange={(date: any) => {
                                                    handleDataChange('pago', 'fim', date.toISOString());
                                                    input.onChange(date);
                                                }}
                                                validate={undefined}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </Form>
            </DialogContent>
            <DialogActions>
                <Box padding={2} display="flex" gridGap={4}>
                    <Button variant="outlined" color="primary" onClick={closeModal}>
                        Fechar
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleSubmit}
                        fullWidth
                        disabled={
                            selectedTenants.length === 0
                        }
                    >
                        Gerar Relatório
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default SolicitacaoPagamentoPage;